import { useState } from "react";
import axios from "axios";
import { Button } from "../../../design/button";
import { Text } from "../../../design/text";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "../../../design/dialog";
import { Textarea } from "../../../design/textarea";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";

interface AuctionRecordListItemProps {
  item: any;
  onFixComplete?: () => void;
}

const ReportIssueDialog: React.FC<AuctionRecordListItemProps> = ({
  item,
  onFixComplete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Please describe the issue");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/auction_records/${item._id}/fix`,
        { description }
      );

      setSuccess(true);
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(false);
        setDescription("");
        if (onFixComplete) {
          onFixComplete();
        }
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to submit fix request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsOpen(false);
      setDescription("");
      setError(null);
      setSuccess(false);
    }
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>Report Issue</Button>
      <Dialog open={isOpen} onClose={handleClose} size="lg">
        <DialogTitle>Report & Fix Issue</DialogTitle>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Text className="text-sm font-medium">Auction Record:</Text>
            <Text className="text-sm text-zinc-600 dark:text-zinc-400">
              {item.title} by {item.artist?.name || "Unknown Artist"}
            </Text>
          </div>

          <div className="space-y-2">
            <Text className="text-sm font-medium">Describe the issue:</Text>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="E.g., The artist name is incorrect, the dimensions are wrong, etc."
              rows={4}
              disabled={isSubmitting || success}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
              <XCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
              <Text className="text-sm text-red-600 dark:text-red-400">
                {error}
              </Text>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              <Text className="text-sm text-green-600 dark:text-green-400">
                Issue fixed successfully! The AI has updated the auction record.
              </Text>
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || success || !description.trim()}
          >
            {isSubmitting ? "Fixing..." : "Fix Issue"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportIssueDialog;
